import { useEffect } from "react";

interface SeoProps {
  title: string;
  description: string;
  canonical: string;
  keywords?: string;
  ogImage?: string;
  jsonLd?: unknown;
}

const DEFAULT_OG_IMAGE =
  "https://static.readdy.ai/image/5a7bf947e7865fcd0423f5d91cb65f0c/3a568d363ad4fe6cbe485ed9bae130d4.jpeg";

const JSONLD_ID = "readdy-page-jsonld";

export default function Seo({ title, description, canonical, keywords, ogImage, jsonLd }: SeoProps) {
  const jsonLdString = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    document.title = title;

    const setMeta = (key: string, content: string, useProperty = false) => {
      const attr = useProperty ? "property" : "name";
      let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    if (keywords) setMeta("keywords", keywords);
    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:url", canonical, true);
    setMeta("og:type", "website", true);
    setMeta("og:locale", "pt_BR", true);
    setMeta("og:site_name", "Eco Tour Turismo", true);
    setMeta("og:image", ogImage || DEFAULT_OG_IMAGE, true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", ogImage || DEFAULT_OG_IMAGE);

    let canonicalEl = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement("link");
      canonicalEl.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute("href", canonical);

    document.getElementById(JSONLD_ID)?.remove();
    if (jsonLdString) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = JSONLD_ID;
      script.textContent = jsonLdString;
      document.head.appendChild(script);
    }

    return () => {
      document.getElementById(JSONLD_ID)?.remove();
    };
  }, [title, description, canonical, keywords, ogImage, jsonLdString]);

  return null;
}